import { auth, removeCognitoCookies } from '../services/auth';
import {
  updateUserAllRebates,
  updateUserVehicleOwn,
} from '../utils/rebatesHelpers';
import {
  afterLoginRedirect,
  needSkipPhoneConfirmation,
} from '../containers/auth/Signup/utils';
import { Permission, User } from '../shared/types/models';
import { MutableRefObject, useCallback } from 'react';
import { IConfirmMFA } from '../containers/auth/Login/MFADialog';
import { IConfirmIdentityDialog } from '../containers/auth/Login/ConfirmIdentityDialog';
import Rest from '../services/rest';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { userAtom, userClientRebatesNamesAtom } from '../recoil/atoms';
import { useRouter } from 'next/router';
import { useFBTrackCompleteRegistration } from './useFBTrackCompleteRegistration';
import { useGTagTrackCompleteRegistration } from './useGTagTrackCompleteRegistration';
import {
  userRebatesCategoriesAnswersSelector,
  userVehicleOwnSelector,
} from '../recoil/selectors';

const rest = new Rest();

type HandleSignupProps = {
  user: {
    email: string;
    password: string;
    phone?: string;
  };
  refMFA?: MutableRefObject<IConfirmMFA>;
  refPhoneConfirmation?: MutableRefObject<IConfirmIdentityDialog>;
  returnTo?: string;
};

const isConsumer = (user: User) =>
  user.permissions.map((p) => p.name).includes(Permission.user);

export const useSignup = () => {
  const setUser = useSetRecoilState(userAtom);
  const router = useRouter();

  const { fbTrackConsumerSignup } = useFBTrackCompleteRegistration();
  const { gtagTrackConsumerSignup } = useGTagTrackCompleteRegistration();

  const rebatesAnswers = useRecoilValueLoadable(
    userRebatesCategoriesAnswersSelector,
  ).valueMaybe();
  const rebatesNames = useRecoilValueLoadable(
    userClientRebatesNamesAtom,
  ).valueMaybe();
  const vehicleOwn = useRecoilValueLoadable(
    userVehicleOwnSelector,
  ).valueMaybe();

  const useCallback1 = useCallback(
    async ({
      user: userData,
      refMFA,
      refPhoneConfirmation,
      returnTo,
    }: HandleSignupProps) => {
      removeCognitoCookies();

      if (
        refPhoneConfirmation?.current &&
        !needSkipPhoneConfirmation(userData.email)
      ) {
        await refPhoneConfirmation?.current?.requestConfirmation(
          {
            email: userData.email,
            password: userData.password,
            phone: userData?.phone,
          },
          { openOnly: false },
        );
      }

      await auth.login(
        userData.email,
        userData.password,
        refMFA?.current?.getCode,
      );
      await Promise.all([
        updateUserAllRebates(rebatesNames, rebatesAnswers),
        updateUserVehicleOwn(vehicleOwn),
      ]);
      const user = await rest.getUserProfile();

      setUser(user);
      if (isConsumer(user)) {
        fbTrackConsumerSignup();
        gtagTrackConsumerSignup();
      }
      afterLoginRedirect(user, router, returnTo);
    },
    [
      fbTrackConsumerSignup,
      gtagTrackConsumerSignup,
      rebatesAnswers,
      rebatesNames,
      router,
      setUser,
      vehicleOwn,
    ],
  );
  return useCallback1;
};
